// =============================================================================
// features/sales/agenda/components/RescheduleAppointmentPanel.tsx —
// Reagendamento inline do drawer (doc 26 §8: cria um SUCESSOR, encerra o
// atual — nunca edita `scheduledAt` no lugar).
//
// Depois de reagendar com sucesso, o painel mostra o HISTÓRICO do
// compromisso substituído (doc 26 §4.1/§22 "reagendamento preserva autoria
// e histórico" — DoD do slot: "reagendamento mostra o histórico do
// compromisso anterior, não o apaga").
// =============================================================================

import type { Appointment, ContactChannel } from '@elemento/shared-schemas';
import { CONTACT_CHANNELS } from '@elemento/shared-schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import { orgLocalToUtcIso, utcIsoToOrgLocalParts } from '../../components/dateTimeOrgTz';
import { FormTextarea } from '../../components/FormTextarea';
import { useRescheduleAppointment } from '../hooks';
import { CHANNEL_LABELS } from '../labels';
import { formatDateTimeOrgTz } from '../timezone';

const CHANNEL_OPTIONS = CONTACT_CHANNELS.map((c: ContactChannel) => ({
  value: c,
  label: CHANNEL_LABELS[c],
}));

const RescheduleFormSchema = z.object({
  date: z.string().min(1, 'Informe a data'),
  time: z.string().min(1, 'Informe a hora'),
  channel: z.string().optional(),
  reason: z.string().max(500, 'Máximo 500 caracteres').optional(),
  notes: z.string().max(4000, 'Máximo 4000 caracteres').optional(),
});

type RescheduleFormValues = z.infer<typeof RescheduleFormSchema>;

export interface RescheduleAppointmentPanelProps {
  appointment: Appointment;
  onDone: () => void;
  onCancel: () => void;
  onDirtyChange: (dirty: boolean) => void;
}

export function RescheduleAppointmentPanel({
  appointment,
  onDone,
  onCancel,
  onDirtyChange,
}: RescheduleAppointmentPanelProps): React.JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<RescheduleFormValues>({
    resolver: zodResolver(RescheduleFormSchema),
    defaultValues: {
      ...utcIsoToOrgLocalParts(appointment.scheduledAt),
      channel: '',
      reason: '',
      notes: '',
    },
  });

  React.useEffect(() => onDirtyChange(isDirty), [isDirty, onDirtyChange]);

  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [previous, setPrevious] = React.useState<Appointment | null>(null);

  const { mutate, isPending } = useRescheduleAppointment({
    onSuccess: (response) => {
      setSubmitError(null);
      setPrevious(response.previousAppointment);
    },
    onError: (error) =>
      setSubmitError(error.message || 'Não foi possível reagendar. Tente novamente.'),
  });

  function onSubmit(values: RescheduleFormValues): void {
    const scheduledAt = orgLocalToUtcIso(values.date, values.time);
    if (!scheduledAt) {
      setSubmitError('Data e hora inválidas.');
      return;
    }

    setSubmitError(null);
    mutate({
      id: appointment.id,
      body: {
        scheduledAt,
        ...(values.channel ? { channel: values.channel as ContactChannel } : {}),
        ...(values.reason?.trim() ? { reason: values.reason.trim() } : {}),
        ...(values.notes?.trim() ? { notes: values.notes.trim() } : {}),
      },
    });
  }

  // ── Estado de sucesso: histórico do compromisso substituído, nunca apagado ──
  if (previous) {
    return (
      <div className="flex flex-col gap-4">
        <div
          role="status"
          className="rounded-sm px-4 py-3 border-l-[3px]"
          style={{
            borderColor: 'var(--success)',
            background: 'var(--success-bg)',
            boxShadow: 'var(--elev-1)',
          }}
        >
          <p className="font-sans font-semibold text-sm" style={{ color: 'var(--success)' }}>
            Compromisso reagendado
          </p>
        </div>

        <div
          className="rounded-sm px-4 py-3"
          style={{ background: 'var(--bg-elev-2)', boxShadow: 'var(--elev-1)' }}
        >
          <p className="font-sans text-xs font-bold uppercase tracking-[0.08em] text-ink-3 mb-1.5">
            Compromisso anterior (histórico)
          </p>
          <p className="font-mono text-sm text-ink-2">
            {formatDateTimeOrgTz(previous.scheduledAt)}
          </p>
          <p className="font-sans text-xs text-ink-3 mt-0.5">
            Encerrado — substituído por este reagendamento.
          </p>
        </div>

        <Button type="button" variant="primary" onClick={onDone} className="justify-center">
          Fechar
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
      noValidate
      className="flex flex-col gap-4"
    >
      <p className="font-sans text-xs text-ink-3">
        Reagendar cria um novo compromisso e encerra o atual — o histórico fica preservado, não é
        apagado.
      </p>

      <div className="grid grid-cols-2 gap-4">
        <Input
          id="reschedule-date"
          label="Nova data"
          type="date"
          required
          disabled={isPending}
          error={errors.date?.message}
          {...register('date')}
        />
        <Input
          id="reschedule-time"
          label="Nova hora"
          type="time"
          required
          disabled={isPending}
          error={errors.time?.message}
          {...register('time')}
        />
      </div>

      <Select
        id="reschedule-channel"
        label="Canal"
        placeholder={`Mesmo canal (${CHANNEL_LABELS[appointment.channel]})`}
        options={CHANNEL_OPTIONS}
        disabled={isPending}
        {...register('channel')}
      />

      <Input
        id="reschedule-reason"
        label="Motivo"
        placeholder={appointment.reason ?? 'Opcional'}
        disabled={isPending}
        error={errors.reason?.message}
        {...register('reason')}
      />

      <FormTextarea
        id="reschedule-notes"
        label="Observação do novo compromisso"
        hint="Não é herdada do compromisso anterior — reafirme se necessário."
        disabled={isPending}
        error={errors.notes?.message}
        {...register('notes')}
      />

      {submitError && (
        <div
          role="alert"
          className="rounded-sm px-4 py-3 border-l-[3px]"
          style={{
            borderColor: 'var(--danger)',
            background: 'var(--danger-bg)',
            boxShadow: 'var(--elev-1)',
          }}
        >
          <p className="font-sans font-semibold text-sm text-danger">Erro ao reagendar</p>
          <p className="font-sans text-xs text-ink-2 mt-0.5">{submitError}</p>
        </div>
      )}

      <div className="flex gap-2.5">
        <Button
          type="submit"
          variant="primary"
          disabled={isPending}
          className="flex-1 justify-center"
        >
          {isPending
            ? 'Reagendando…'
            : submitError
              ? 'Tentar novamente'
              : 'Confirmar reagendamento'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isPending}>
          Voltar
        </Button>
      </div>
    </form>
  );
}
